import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { API_TAGS } from '@/shared/api';

import { createAiAgentChat, fetchAiAgentChat, fetchAiAgentChats, fetchAiAgentMessages } from './api';

/** Размер порции списка чатов и переписки */
const PAGE_SIZE = 50;

/**
 * Переписка запрашивается с конца: последние сообщения доступны, не зная общего количества,
 * а движение к началу идёт теми же порциями. Порция приходит от свежего сообщения к раннему.
 */
const MESSAGES_SORT = 'createdAt:desc';

export const aiAgentChatKeys = {
  all: [API_TAGS.AI_AGENT_CHATS] as const,
  lists: () => [...aiAgentChatKeys.all, 'list'] as const,
  details: () => [...aiAgentChatKeys.all, 'detail'] as const,
  detail: (id: number) => [...aiAgentChatKeys.details(), id] as const,
  messages: (chatId: number) => [...aiAgentChatKeys.all, chatId, 'messages'] as const,
};

/**
 * Обе ленты растут с того конца, с которого берётся первая порция, а порции отбираются смещением:
 * отправленное сообщение и поднявшийся в списке чат сдвигают выдачу, и соседние порции успевают
 * перекрыться, пока запрошенные заново порции не пришли.
 */
const dedupeById = <T extends { id: number }>(items: T[]) => {
  const byId = new Map<number, T>();
  items.forEach((item) => {
    if (!byId.has(item.id)) byId.set(item.id, item);
  });

  return [...byId.values()];
};

/** Список чатов пользователя, догружаемый порциями; `chats` — накопленная выдача */
export const useInfiniteAiAgentChats = () => {
  const query = useInfiniteQuery({
    queryKey: aiAgentChatKeys.lists(),
    queryFn: ({ pageParam }) => fetchAiAgentChats({ limit: PAGE_SIZE, offset: pageParam * PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.items.length < PAGE_SIZE) return undefined;
      return lastPage.total <= PAGE_SIZE * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  const chats = useMemo(() => dedupeById(query.data?.pages.flatMap((page) => page.items) ?? []), [query.data]);

  return { ...query, chats };
};

export const useAiAgentChat = (id: number) => {
  return useQuery({
    queryKey: aiAgentChatKeys.detail(id),
    queryFn: () => fetchAiAgentChat(id),
    enabled: id > 0,
  });
};

/**
 * Переписка чата, догружаемая порциями от последних сообщений к первым;
 * `messages` — накопленная выдача в хронологическом порядке.
 */
export const useInfiniteAiAgentMessages = (chatId: number) => {
  const query = useInfiniteQuery({
    queryKey: aiAgentChatKeys.messages(chatId),
    queryFn: ({ pageParam }) =>
      fetchAiAgentMessages(chatId, { limit: PAGE_SIZE, offset: pageParam * PAGE_SIZE, sort: MESSAGES_SORT }),
    initialPageParam: 0,
    enabled: chatId > 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (lastPage.items.length < PAGE_SIZE) return undefined;
      return lastPage.total <= PAGE_SIZE * (lastPageParam + 1) ? undefined : lastPageParam + 1;
    },
  });

  // Порции идут от конца переписки к её началу — лента показывает их в обратном порядке
  const messages = useMemo(
    () => dedupeById(query.data?.pages.flatMap((page) => page.items) ?? []).reverse(),
    [query.data],
  );

  return { ...query, messages };
};

export const useCreateAiAgentChat = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAiAgentChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiAgentChatKeys.lists() });
    },
  });
};
